import IconButton from 'components/Luxkit/Button/IconButton'
import LineMultiplyIcon from 'components/Luxkit/Icons/line/LineMultiplyIcon'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import useHasMounted from 'hooks/useHasMounted'
import { rem } from 'polished'
import React, { CSSProperties, MouseEventHandler, PropsWithChildren, forwardRef, useCallback } from 'react'
import cn from 'clsx'
import styled, { keyframes } from 'styled-components'
import Clickable from '../Clickable/Clickable'
import useDelayedTogglingBoolean from 'hooks/useDelayedTogglingBoolean'
import zIndex from 'styles/tools/z-index'
import eases from 'styles/tools/eases'
import Group from 'components/utils/Group'
import Heading from 'components/Luxkit/Typography/Heading'
import { mediaHoverable } from 'lib/theme/mediaQueries'

const ANIMATION_DURATION_MS = 300

const SlideFromBottom = keyframes`
  from {
    transform: translate(-50%, 120%);
    visibility: visible;
  }

  to {
    transform: translate(-50%, 0%);
    visibility: visible;
  }
`
const SlideToBottom = keyframes`
  from {
    transform: translate(-50%, 0%);
    visibility: visible;
  }

  to {
    transform: translate(-50%, 120%);
    visibility: hidden;
  }
`

const SlideFromRight = keyframes`
  from {
    transform: translateX(120%);
    visibility: visible;
  }

  to {
    transform: translateX(0%);
    visibility: visible;
  }
`
const SlideToRight = keyframes`
  from {
    transform: translateX(0%);
    visibility: visible;
  }

  to {
    transform: translateX(120%);
    visibility: hidden;
  }
`

const Container = styled.div`
  ${mediaQueryUp.tablet} {
    height: 0;
    width: 100%;
    position: sticky;
    top: 0; // do not delete this
    z-index: ${zIndex.stickyPromptCard};
  }
`

const CardRoot = styled(Clickable)`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  max-width: calc(100vw - ${rem(32)});
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.default.five};
  box-shadow: ${props => props.theme.shadow.bottom.large};
  border-radius: ${props => props.theme.borderRadius.S};
  padding: ${rem(20)};
  width: 100vw;
  transition: border-color 200ms ease;

  ${mediaQueryUp.tablet} {
    width: ${rem(360)};
    padding: ${rem(16)};
  }

  ${mediaHoverable} {
    &:hover {
      border-color: ${props => props.theme.palette.neutral.default.one};
    }
  }

  animation-duration: ${ANIMATION_DURATION_MS}ms;
  animation-iteration-count: 1;
  animation-direction: alternate;
  animation-timing-function: ${eases.power2.out};
  visibility: hidden;

  &.transition-in {
    visibility: visible;
  }

  ${mediaQueryDown.mobile} {
    /* center at bottom */
    z-index: ${zIndex.stickyPromptCard};
    position: fixed;
    bottom: ${rem(8)};
    left: 50%;
    transform: translateX(-50%);
    animation-name: ${SlideToBottom};

    &.transition-in {
      animation-name: ${SlideFromBottom};
    }
  }

  ${mediaQueryUp.tablet} {
    /* top right corner */
    position: absolute;
    top: var(--prompt-card-sticky-top);
    right: 0;
    margin: ${rem(16)};
    animation-name: ${SlideToRight};

    &.transition-in {
      animation-name: ${SlideFromRight};
    }
  }
`

const CloseButton = styled(IconButton)`
  transform: translate(${rem(4)}, ${rem(-4)});
`

interface Props {
  title: string
  show: boolean;
  to: string;
  onClose: () => void;
  onClick: () => void;
  stickyTop?: number;
}

const StickyPromptCard = forwardRef<HTMLButtonElement, PropsWithChildren<Props>>((props, ref) => {
  const {
    title,
    show,
    to,
    children,
    onClose,
    onClick,
    stickyTop = 0,
  } = props
  const isMounted = useHasMounted()

  const shouldRender = useDelayedTogglingBoolean(show, {
    offDelay: ANIMATION_DURATION_MS,
  })

  const handleClose = useCallback<MouseEventHandler>((event) => {
    event.preventDefault()
    event.stopPropagation()

    onClose()
  }, [onClose])

  if (!shouldRender) {
    return null
  }

  if (!isMounted) {
    return null
  }

  return (
    <Container style={{ '--prompt-card-sticky-top': rem(stickyTop) } as CSSProperties}>
      <CardRoot ref={ref} to={to} className={cn({ 'transition-in': show })} onClick={onClick}>
        <Group direction="horizontal" gap={8} horizontalAlign="space-between">
          <Heading variant="heading6" wrap="truncate">{title}</Heading>
          <CloseButton
            kind="tertiary"
            onClick={handleClose}
            size="small"
            shape="square"
          >
            <LineMultiplyIcon />
          </CloseButton>
        </Group>
        {children}
      </CardRoot>
    </Container>
  )
})

export default StickyPromptCard
